'use client';

import React, { useEffect, useRef } from 'react';
import HeroSection from './thank-you/components/HeroSection';
import SkillsSection from './thank-you/components/SkillsSection';
import ProjectsSection from './thank-you/components/ProjectsSection';
import ExperienceSection from './thank-you/components/ExperienceSection';
import ContactSection from './thank-you/components/ContactSection';
import LanguageSwitcher from './thank-you/components/LanguageSwitcher';
import Footer from '@/components/Footer';
import { LanguageProvider } from '@/context/LanguageContext';
import { PortfolioDataProvider, usePortfolioData } from '@/context/PortfolioDataContext';
import { trackEvent } from '@/components/GoogleAnalytics';

function PortfolioPageContent() {
  const revealRefs = useRef<NodeListOf<Element> | null>(null);
  const { loading: _loading } = usePortfolioData();

  useEffect(() => {
    revealRefs.current = document.querySelectorAll('.reveal');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    revealRefs.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [_loading]);

  useEffect(() => {
    const sections = [
      { id: 'hero', label: 'Hero' },
      { id: 'skills', label: 'Skills' },
      { id: 'projects', label: 'Projects' },
      { id: 'experience', label: 'Experience' },
      { id: 'contact', label: 'Contact' },
    ];

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section');
            if (sectionId) {
              trackEvent('section_view', {
                section_name: sectionId,
                page_path: window.location.pathname,
              });
            }
          }
        });
      },
      { threshold: 0.4 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) sectionObserver.observe(el);
    });

    return () => sectionObserver.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen bg-slate-void overflow-x-hidden">
      {/* Global noise texture */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      <LanguageSwitcher />

      <main className="relative z-10">
        <section id="hero" data-section="Hero">
          <HeroSection />
        </section>
        <section id="skills" data-section="Skills">
          <SkillsSection />
        </section>
        <section id="projects" data-section="Projects">
          <ProjectsSection />
        </section>
        <section id="experience" data-section="Experience">
          <ExperienceSection />
        </section>
        <section id="contact" data-section="Contact">
          <ContactSection />
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default function PortfolioPage() {
  return (
    <LanguageProvider>
      <PortfolioDataProvider>
        <PortfolioPageContent />
      </PortfolioDataProvider>
    </LanguageProvider>
  );
}
